<template>
  <div class="container artwork-workflow-view coredeck-page">
    <div class="card workflow-shell-card">
      <div class="card-content workflow-shell-content">
        <!-- ===============================================
             HEADER
             =============================================== -->
        <div class="workflow-header">
          <div>
            <h4>Artwork & Social Workflow</h4>

            <p class="grey-text text-darken-1">
              Create artwork requests, track progress, proof artwork, and manage social
              posting.
            </p>
          </div>

          <button
            class="btn blue-grey darken-2"
            type="button"
            :disabled="loading || !resolvedVenueId"
            @click="loadRequests"
          >
            Refresh
          </button>
        </div>

        <!-- ===============================================
             ERRORS / VENUE STATE
             =============================================== -->
        <div
          v-if="!resolvedVenueId"
          class="card-panel red lighten-5 red-text text-darken-3 state-panel"
        >
          No venue selected. Please select a venue from Venue Snapshot first.
        </div>

        <div
          v-if="error"
          class="card-panel red lighten-5 red-text text-darken-3 state-panel"
        >
          {{ error }}
        </div>

        <!-- ===============================================
             CREATE REQUEST
             VENUE USERS ONLY
             =============================================== -->
        <ArtworkRequestForm
          v-if="currentUserRoleLoaded && !currentUserIsSocialMedia"
          v-model:title="newRequest.title"
          v-model:notes="newRequest.notes"
          v-model:due-date="newRequest.dueDate"
          :venue-name="displayVenueName"
          :saving="saving"
          :disabled="!resolvedVenueId"
          @create="createRequest"
        />

        <p v-if="loading" class="grey-text loading-text">Loading workflow requests...</p>

        <!-- ===============================================
             ACTIVE REQUESTS
             =============================================== -->
        <div
          v-if="!loading && resolvedVenueId && activeRequests.length === 0"
          class="empty-state-card"
        >
          No active artwork requests found for this venue.
        </div>

        <div v-if="activeRequests.length" class="row request-grid">
          <ArtworkRequestCard
            v-for="request in activeRequests"
            :key="request.id"
            :request="request"
            :workflow-stages="workflowStages"
            :updating="updatingId === request.id"
            :uploading="uploadingId === request.id"
            :sending-proof="sendingProofId === request.id"
            :can-upload-artwork="currentUserIsSocialMedia"
            :can-send-proof="currentUserIsSocialMedia"
            :can-approve="canApproveRequest(request)"
            :can-mark-posted="canMarkSocialPosted(request)"
            :can-close-request="currentUserIsSocialMedia"
            @toggle-stage="toggleStage"
            @upload-artwork="uploadArtwork"
            @send-proof="sendProof"
            @close-request="closeRequest"
            @delete-request="deleteRequest"
            @preview="openPreview"
          />
        </div>

        <!-- ===============================================
             COMPLETED REQUESTS
             OWN COMPONENT
             =============================================== -->
        <ArtworkCompletedTable
          v-if="completedRequests.length"
          :requests="completedRequests"
          :workflow-stages="workflowStages"
          @preview="openPreview"
        />
      </div>
    </div>

    <!-- ===============================================
         ARTWORK PREVIEW
         =============================================== -->
    <ArtworkPreviewModal :request="previewRequest" @close="closePreview" />
  </div>
</template>

<script>
import { doc, getDoc } from "firebase/firestore";

import ArtworkCompletedTable from "@/components/artwork/ArtworkCompletedTable.vue";
import ArtworkPreviewModal from "@/components/artwork/ArtworkPreviewModal.vue";
import ArtworkRequestCard from "@/components/artwork/ArtworkRequestCard.vue";
import ArtworkRequestForm from "@/components/artwork/ArtworkRequestForm.vue";

import { auth, db } from "@/firebase";

import {
  closeArtworkRequest,
  createArtworkRequest,
  deleteArtworkRequest,
  fetchArtworkRequests,
  fetchVenue,
  saveArtworkToRequest,
  sendArtworkProof,
  updateArtworkWorkflow,
} from "@/scripts/artwork/artworkRequests";

import {
  recoverStoredArtwork,
  uploadArtworkFile,
} from "@/scripts/artwork/artworkStorage";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

/* =========================================================
   Workflow
   ========================================================= */

const WORKFLOW_STAGES = [
  {
    key: "venueRequest",
    label: "Venue request",
  },

  {
    key: "artworkCreated",
    label: "Artwork created",
  },

  {
    key: "artworkSentToProof",
    label: "Artwork sent to proof",
  },

  {
    key: "artworkApproved",
    label: "Artwork approved",
  },

  {
    key: "socialContentPosted",
    label: "Social content posted",
  },
];

const LOCKED_WORKFLOW_STAGES = ["venueRequest", "artworkCreated", "artworkSentToProof"];

function blankWorkflow() {
  return {
    venueRequest: true,
    artworkCreated: false,
    artworkSentToProof: false,
    artworkApproved: false,
    socialContentPosted: false,
  };
}

/* =========================================================
   Helpers
   ========================================================= */

function unwrapMaybeRef(value) {
  return value && typeof value === "object" && "value" in value ? value.value : value;
}

function firstString(...values) {
  for (const value of values) {
    const unwrapped = unwrapMaybeRef(value);

    if (typeof unwrapped === "string" && unwrapped.trim()) {
      return unwrapped.trim();
    }
  }

  return "";
}

function firstVenueName(...values) {
  for (const value of values) {
    const unwrapped = unwrapMaybeRef(value);

    if (typeof unwrapped === "string" && unwrapped.trim()) {
      const trimmed = unwrapped.trim();

      if (trimmed !== "undefined" && trimmed !== "null") {
        return trimmed;
      }
    }
  }

  return "";
}

function localStorageValue(...keys) {
  try {
    for (const key of keys) {
      const value = localStorage.getItem(key);

      if (value && value.trim()) {
        return value.trim();
      }
    }
  } catch {
    return "";
  }

  return "";
}

function flagIsTrue(value) {
  if (value === true) {
    return true;
  }

  return (
    String(value || "")
      .trim()
      .toLowerCase() === "true"
  );
}

function socialMediaRole(profile = {}, claims = {}) {
  const userType = String(profile.user_type || profile.userType || profile.role || "")
    .trim()
    .toLowerCase();

  return (
    flagIsTrue(claims.social_media) ||
    flagIsTrue(claims.socialMedia) ||
    flagIsTrue(claims.is_social_media) ||
    flagIsTrue(profile.isSocialMedia) ||
    flagIsTrue(profile.is_social_media) ||
    flagIsTrue(profile.social_media) ||
    userType === "social_media" ||
    userType === "social-media" ||
    userType === "social media"
  );
}

function artworkList(request) {
  const artworks = Array.isArray(request?.artworks)
    ? request.artworks.filter((item) => item && item.downloadUrl)
    : [];

  if (artworks.length) {
    return artworks;
  }

  if (request?.artwork?.downloadUrl) {
    return [request.artwork];
  }

  return [];
}

/* =========================================================
   Component
   ========================================================= */

export default {
  name: "ArtworkWorkflowTracker",

  components: {
    ArtworkCompletedTable,
    ArtworkPreviewModal,
    ArtworkRequestCard,
    ArtworkRequestForm,
  },

  props: {
    venueId: {
      type: String,
      default: "",
    },

    venue: {
      type: String,
      default: "",
    },
  },

  setup() {
    const venueContext = useVenueContext();

    return {
      venueContext,
    };
  },

  data() {
    return {
      workflowStages: WORKFLOW_STAGES,

      requests: [],

      loading: false,
      saving: false,

      updatingId: null,
      uploadingId: null,
      sendingProofId: null,

      error: "",

      previewRequest: null,

      venueDocName: "",
      venueDocLoadedFor: "",

      dropdownVenueId: "",
      venueChangeHandler: null,

      currentUserRoleLoaded: false,

      currentUserIsSocialMedia: false,

      currentUserUid: "",
      currentUserEmail: "",

      newRequest: {
        title: "",
        notes: "",
        dueDate: "",
      },
    };
  },

  computed: {
    routeVenueId() {
      return firstString(
        this.$route?.params?.venueId,

        this.$route?.query?.venueId,

        this.$route?.query?.venue
      );
    },

    contextVenueId() {
      return firstString(
        this.venueContext?.activeVenueId,

        this.venueContext?.venueId,

        this.venueContext?.selectedVenueId,

        this.venueContext?.activeVenue?.id,

        this.venueContext?.activeVenue?.venueId,

        this.venueContext?.activeVenue?.venue_id,

        this.venueContext?.selectedVenue?.id,

        this.venueContext?.selectedVenue?.venueId,

        this.venueContext?.selectedVenue?.venue_id
      );
    },

    storedVenueId() {
      return localStorageValue(
        "coredeck_lastVenueId",
        "activeVenueId",
        "venueId",
        "selectedVenueId"
      );
    },

    resolvedVenueId() {
      return firstString(
        this.dropdownVenueId,
        this.contextVenueId,
        this.venueId,
        this.venue,
        this.routeVenueId,
        this.storedVenueId
      );
    },

    resolvedVenueName() {
      return firstVenueName(
        this.venueDocName,

        this.venueContext?.activeVenue?.venue_name,

        this.venueContext?.selectedVenue?.venue_name,

        this.venueContext?.activeVenue?.name,

        this.venueContext?.selectedVenue?.name,

        this.venueContext?.activeVenue?.title,

        this.venueContext?.selectedVenue?.title,

        this.venueContext?.activeVenue?.label,

        this.venueContext?.selectedVenue?.label
      );
    },

    displayVenueName() {
      if (!this.resolvedVenueId) {
        return "Not selected";
      }

      return this.resolvedVenueName || "Loading venue...";
    },

    canCreateRequest() {
      return Boolean(
        this.currentUserRoleLoaded &&
          !this.currentUserIsSocialMedia &&
          this.resolvedVenueId &&
          this.newRequest.title.trim()
      );
    },

    /*
     * Only active requests render as cards.
     */
    activeRequests() {
      return this.requests.filter(
        (request) => request.closed !== true && request.status !== "completed"
      );
    },

    /*
     * Completed table owns the ordering.
     * The tracker only decides which records belong there.
     */
    completedRequests() {
      return this.requests.filter(
        (request) => request.closed === true || request.status === "completed"
      );
    },
  },

  watch: {
    resolvedVenueId: {
      immediate: true,

      handler(newVenueId, oldVenueId) {
        if (!newVenueId) {
          this.requests = [];
          this.venueDocName = "";
          this.venueDocLoadedFor = "";

          return;
        }

        if (newVenueId !== oldVenueId) {
          this.venueDocName = "";

          this.venueDocLoadedFor = "";

          this.loadVenueDisplayName();
          this.loadRequests();
        }
      },
    },
  },

  async mounted() {
    this.venueChangeHandler = (event) => {
      const nextVenueId = firstString(event?.detail);

      if (!nextVenueId) {
        return;
      }

      this.dropdownVenueId = nextVenueId;
    };

    window.addEventListener("ops-venue-changed", this.venueChangeHandler);

    await this.loadCurrentUserRole();
  },

  beforeUnmount() {
    if (this.venueChangeHandler) {
      window.removeEventListener("ops-venue-changed", this.venueChangeHandler);
    }
  },

  methods: {
    /* =====================================================
       User role
       ===================================================== */

    async loadCurrentUserRole() {
      this.currentUserRoleLoaded = false;

      this.currentUserIsSocialMedia = false;

      this.currentUserUid = "";
      this.currentUserEmail = "";

      try {
        const user = auth.currentUser;

        if (!user?.uid) {
          return;
        }

        this.currentUserUid = user.uid;

        this.currentUserEmail = String(user.email || "")
          .trim()
          .toLowerCase();

        let claims = {};

        try {
          const tokenResult = await user.getIdTokenResult();

          claims = tokenResult?.claims || {};
        } catch (err) {
          console.warn("[ArtworkWorkflow] Claims load failed", err);
        }

        let profile = {};

        try {
          const profileSnapshot = await getDoc(doc(db, "users", user.uid));

          if (profileSnapshot.exists()) {
            profile = profileSnapshot.data() || {};
          }
        } catch (err) {
          console.warn("[ArtworkWorkflow] User profile load failed", err);
        }

        this.currentUserIsSocialMedia = socialMediaRole(profile, claims);
      } finally {
        this.currentUserRoleLoaded = true;
      }
    },

    /* =====================================================
       Artwork helpers
       ===================================================== */

    requestArtworks(request) {
      return artworkList(request);
    },

    hasRequestArtwork(request) {
      return this.requestArtworks(request).length > 0;
    },

    /* =====================================================
       Permissions
       ===================================================== */

    isRequestOwner(request) {
      if (!request || this.currentUserIsSocialMedia) {
        return false;
      }

      if (request.requestedByUid) {
        return request.requestedByUid === this.currentUserUid;
      }

      const requestedEmail = String(request.requestedByEmail || "")
        .trim()
        .toLowerCase();

      return Boolean(
        requestedEmail &&
          this.currentUserEmail &&
          requestedEmail === this.currentUserEmail
      );
    },

    canApproveRequest(request) {
      return Boolean(
        this.isRequestOwner(request) && request.workflow?.artworkSentToProof === true
      );
    },

    canMarkSocialPosted(request) {
      return Boolean(
        this.currentUserIsSocialMedia && request.workflow?.artworkApproved === true
      );
    },

    /* =====================================================
       Venue
       ===================================================== */

    async loadVenueDisplayName() {
      const venueId = this.resolvedVenueId;

      if (!venueId) {
        return;
      }

      if (this.venueDocLoadedFor === venueId && this.venueDocName) {
        return;
      }

      try {
        const venueData = await fetchVenue(venueId);

        if (!venueData) {
          this.venueDocName = "";

          this.venueDocLoadedFor = venueId;

          return;
        }

        this.venueDocName = firstVenueName(
          venueData.venue_name,
          venueData.name,
          venueData.title,
          venueData.label,
          venueData.displayName,
          venueData.display_name
        );

        this.venueDocLoadedFor = venueId;
      } catch (err) {
        console.error("Failed to load venue display name", err);

        this.venueDocName = "";

        this.venueDocLoadedFor = venueId;
      }
    },

    /* =====================================================
       Load requests
       ===================================================== */

    async loadRequests() {
      const venueId = this.resolvedVenueId;

      if (!venueId) {
        return;
      }

      this.loading = true;
      this.error = "";

      try {
        await this.loadVenueDisplayName();

        const loadedRequests = await fetchArtworkRequests(venueId);

        if (venueId !== this.resolvedVenueId) {
          return;
        }

        this.requests = loadedRequests;

        await Promise.all(
          this.requests.map((request) => this.ensureStoredArtwork(request, venueId))
        );
      } catch (err) {
        console.error("Failed to load artwork requests", err);

        this.error = err.message || "Failed to load artwork requests.";
      } finally {
        this.loading = false;
      }
    },

    /* =====================================================
       Legacy artwork recovery
       ===================================================== */

    async ensureStoredArtwork(request, venueId) {
      if (!request?.id || !venueId) {
        return;
      }

      if (this.hasRequestArtwork(request)) {
        return;
      }

      if (!request.artwork?.storagePath) {
        return;
      }

      try {
        const recoveredArtwork = await recoverStoredArtwork({
          venueId,

          requestId: request.id,

          artwork: request.artwork,
        });

        if (!recoveredArtwork) {
          return;
        }

        const nextWorkflow = {
          ...blankWorkflow(),

          ...(request.workflow || {}),

          artworkCreated: true,
        };

        await saveArtworkToRequest({
          venueId,

          requestId: request.id,

          artwork: recoveredArtwork,

          workflow: nextWorkflow,
        });

        request.artwork = recoveredArtwork;

        request.artworks = [recoveredArtwork];

        request.workflow = nextWorkflow;
      } catch (err) {
        console.error(`Failed to recover stored artwork for request ${request.id}`, err);

        const code = String(err?.code || "");

        if (code.includes("unauthorized") || code.includes("permission")) {
          this.error =
            "Artwork was found, but your Firebase rules do not allow it to be read.";
        }
      }
    },

    /* =====================================================
       Create request
       ===================================================== */

    async createRequest() {
      if (!this.canCreateRequest) {
        return;
      }

      const venueId = this.resolvedVenueId;

      this.saving = true;
      this.error = "";

      try {
        await this.loadVenueDisplayName();

        const user = auth.currentUser;

        await createArtworkRequest({
          venueId,

          venueName: this.resolvedVenueName || "",

          title: this.newRequest.title.trim(),

          notes: this.newRequest.notes.trim(),

          dueDate: this.newRequest.dueDate || "",

          user,

          workflow: blankWorkflow(),
        });

        this.newRequest = {
          title: "",
          notes: "",
          dueDate: "",
        };

        await this.loadRequests();

        this.$nextTick(() => {
          if (window.M && window.M.updateTextFields) {
            window.M.updateTextFields();
          }
        });
      } catch (err) {
        console.error("Failed to create artwork request", err);

        this.error = err.message || "Failed to create artwork request.";
      } finally {
        this.saving = false;
      }
    },

    /* =====================================================
       Workflow
       ===================================================== */

    async toggleStage(request, stageKey, checked) {
      if (!request?.id || !stageKey) {
        return;
      }

      if (LOCKED_WORKFLOW_STAGES.includes(stageKey)) {
        return;
      }

      if (stageKey === "artworkApproved" && !this.canApproveRequest(request)) {
        return;
      }

      if (stageKey === "socialContentPosted" && !this.canMarkSocialPosted(request)) {
        return;
      }

      this.updatingId = request.id;

      this.error = "";

      try {
        const nextWorkflow = {
          ...blankWorkflow(),

          ...(request.workflow || {}),

          [stageKey]: checked,
        };

        if (stageKey === "artworkApproved" && checked === false) {
          nextWorkflow.socialContentPosted = false;
        }

        await updateArtworkWorkflow({
          venueId: this.resolvedVenueId,

          requestId: request.id,

          workflow: nextWorkflow,
        });

        request.workflow = nextWorkflow;
      } catch (err) {
        console.error("Failed to update workflow stage", err);

        this.error = err.message || "Failed to update workflow stage.";

        await this.loadRequests();
      } finally {
        this.updatingId = null;
      }
    },

    /* =====================================================
       Upload artwork
       ===================================================== */

    async uploadArtwork(request, event) {
      if (!this.currentUserIsSocialMedia) {
        this.error = "Only the social media user can upload artwork.";

        return;
      }

      const files = Array.from(event.target.files || []);

      const venueId = this.resolvedVenueId;

      if (!files.length || !request?.id || !venueId) {
        return;
      }

      this.uploadingId = request.id;

      this.error = "";

      const nextWorkflow = {
        ...blankWorkflow(),

        ...(request.workflow || {}),

        artworkCreated: true,

        artworkSentToProof: false,

        artworkApproved: false,

        socialContentPosted: false,
      };

      const currentArtworks = [...this.requestArtworks(request)];

      try {
        for (const file of files) {
          const artworkPayload = await uploadArtworkFile({
            venueId,

            requestId: request.id,

            file,
          });

          await saveArtworkToRequest({
            venueId,

            requestId: request.id,

            artwork: artworkPayload,

            workflow: nextWorkflow,
          });

          const alreadyPresent = currentArtworks.some(
            (item) => item.storagePath === artworkPayload.storagePath
          );

          if (!alreadyPresent) {
            currentArtworks.push(artworkPayload);
          }

          request.artwork = artworkPayload;

          request.artworks = [...currentArtworks];

          request.workflow = nextWorkflow;
        }

        request.proofSentTo = "";

        request.proofSentAt = null;

        event.target.value = "";

        const filePathInput = event.target
          .closest(".file-field")
          ?.querySelector(".file-path");

        if (filePathInput) {
          filePathInput.value = "";
        }
      } catch (err) {
        console.error("Failed to upload artwork", err);

        this.error = err.message || "Failed to upload artwork.";
      } finally {
        this.uploadingId = null;
      }
    },

    /* =====================================================
       Send proof
       ===================================================== */

    async sendProof(request) {
      if (!this.currentUserIsSocialMedia) {
        this.error = "Only the social media user can send artwork to proof.";

        return;
      }

      if (!request?.id || !this.hasRequestArtwork(request)) {
        this.error = "Upload artwork before sending proof.";

        return;
      }

      const venueId = this.resolvedVenueId;

      this.sendingProofId = request.id;

      this.error = "";

      try {
        const result = await sendArtworkProof({
          venueId,

          requestId: request.id,
        });

        const nextWorkflow = {
          ...blankWorkflow(),

          ...(request.workflow || {}),

          artworkCreated: true,

          artworkSentToProof: true,
        };

        request.workflow = nextWorkflow;

        request.proofSentTo = result?.sentTo || request.requestedByEmail || "";
      } catch (err) {
        console.error("Failed to send artwork proof", err);

        this.error = err.message || "Failed to send artwork proof.";
      } finally {
        this.sendingProofId = null;
      }
    },

    /* =====================================================
       Close request
       SOCIAL MEDIA ONLY
       ===================================================== */

    async closeRequest(request) {
      if (!this.currentUserIsSocialMedia) {
        this.error = "Only the social media user can close artwork requests.";

        return;
      }

      if (!request?.id) {
        return;
      }

      const confirmed = window.confirm(
        `Close artwork request "${request.title}"? It will be moved to Completed Artwork Requests.`
      );

      if (!confirmed) {
        return;
      }

      this.updatingId = request.id;

      this.error = "";

      try {
        const completionData = await closeArtworkRequest({
          venueId: this.resolvedVenueId,

          requestId: request.id,

          user: auth.currentUser,
        });

        /*
         * Updating these values moves the request
         * immediately from activeRequests to
         * completedRequests without needing a reload.
         */
        request.closed = true;

        request.status = "completed";

        request.completedAt = completionData.completedAt;

        request.completedByUid = completionData.completedByUid;

        request.completedByEmail = completionData.completedByEmail;

        if (this.previewRequest?.id === request.id) {
          this.closePreview();
        }
      } catch (err) {
        console.error("Failed to close artwork request", err);

        this.error = err.message || "Failed to close artwork request.";
      } finally {
        this.updatingId = null;
      }
    },

    /* =====================================================
       Delete
       ===================================================== */

    async deleteRequest(request) {
      if (!request?.id) {
        return;
      }

      const confirmed = window.confirm(`Delete artwork request "${request.title}"?`);

      if (!confirmed) {
        return;
      }

      this.updatingId = request.id;

      this.error = "";

      try {
        await deleteArtworkRequest(this.resolvedVenueId, request.id);

        this.requests = this.requests.filter((item) => item.id !== request.id);

        if (this.previewRequest?.id === request.id) {
          this.closePreview();
        }
      } catch (err) {
        console.error("Failed to delete artwork request", err);

        this.error = err.message || "Failed to delete artwork request.";
      } finally {
        this.updatingId = null;
      }
    },

    /* =====================================================
       Preview
       ===================================================== */

    openPreview(request, artwork) {
      if (!request || !artwork?.downloadUrl) {
        return;
      }

      this.previewRequest = {
        ...request,
        artwork,
      };
    },

    closePreview() {
      this.previewRequest = null;
    },
  },
};
</script>

<style scoped>
.artwork-workflow-view {
  width: calc(100% - 64px);
  max-width: 1500px;

  margin: 28px auto;

  box-sizing: border-box;
}

.workflow-shell-card {
  width: 100%;
  margin: 0;

  overflow: hidden;

  border-radius: 14px;
}

.workflow-shell-content {
  padding: 28px !important;
}

.workflow-header {
  display: flex;
  gap: 16px;

  align-items: flex-start;
  justify-content: space-between;

  margin-bottom: 22px;
}

.workflow-header h4 {
  margin-top: 0;
}

.state-panel {
  margin-top: 14px;
}

.loading-text {
  margin-top: 14px;
}

.empty-state-card {
  margin-top: 18px;
  padding: 22px 24px;

  color: #263238;

  background: #f5f5f5;

  border: 1px solid #e0e0e0;
  border-radius: 10px;
}

.request-grid {
  display: flex;
  flex-wrap: wrap;

  margin-top: 8px;
}

@media only screen and (max-width: 992px) {
  .artwork-workflow-view {
    width: calc(100% - 40px);

    margin: 20px auto;
  }
}

@media only screen and (max-width: 600px) {
  .artwork-workflow-view {
    width: calc(100% - 24px);

    margin: 12px auto;
  }

  .workflow-shell-content {
    padding: 18px !important;
  }

  .workflow-header {
    flex-direction: column;
  }

  .workflow-header .btn {
    width: 100%;
    margin: 4px 0;
  }
}
</style>
